// react components
import React, {
    useState,
} from 'react'
import {
    useStripe,
} from '@stripe/react-stripe-js'
import axios from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// components
import {
    LoaderSite,
} from 'components'

// data
import {
    api_url_stripe_subscription_customer_create_web,
    defaultReduxState,
} from 'data'

// services
import {
    axiosErrorHandlerSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
} from 'services'

// props
type ButtonHelper810SiteProps = {
    amount: number
    buttonText: string | undefined
    currency: string
    isRecurent: boolean
    membershipPriceId?: string
    minimumAmount?: number
    productId: string | undefined
    dynamicFormData: { [key: string]: any }
    setDynamicFormErrors: React.Dispatch<{ [key: string]: string }>
    formFields?: Array<{
        name?: string
        required?: boolean
        field_type?: string
    }>
}

// main
export const ButtonHelper810Site: React.FC<ButtonHelper810SiteProps> = React.memo(({
    amount,
    buttonText,
    currency,
    isRecurent,
    membershipPriceId,
    minimumAmount,
    productId,
    dynamicFormData,
    setDynamicFormErrors,
    formFields,
}) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxCacheSite = useSelector((state: defaultReduxState) => state.reduxCacheSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    const deviceType = reduxModalSite.deviceType

    const stripe = useStripe()

    const [isLoading, setIsLoading] = useState(false)

    function handleCheckData() {
        try {
            let checkOk = true
            const errors: { [key: string]: string } = {}
            const numericAmount = Number(amount)
            const minAmount = minimumAmount || 0

            // Validate amount
            if (!membershipPriceId) {
                if (!amount || numericAmount <= 0) {
                    checkOk = false
                    errors.amount = reduxText[7489]
                } else if (numericAmount < minAmount) {
                    checkOk = false
                    errors.amount = `Le montant minimum est de ${minAmount}€`
                }
            }

            // Validate email if present
            const email = dynamicFormData.email || ''
            if (email) {
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                if (!re.test(email)) {
                    checkOk = false
                    errors.email = reduxText[7490]
                }
            }

            // Dynamically validate required fields
            if (formFields) {
                formFields.forEach(field => {
                    if (field.required && field.name) {
                        const value = dynamicFormData[field.name] || ''
                        let isValid = false
                        if (typeof value === 'string') {
                            isValid = value.trim() !== ''
                        } else if (Array.isArray(value)) {
                            isValid = value.length > 0
                        } else if (value && typeof value === 'object') {
                            isValid = true
                        }
                        if (!isValid) {
                            checkOk = false
                            errors[field.name] = reduxText[7489]
                        }
                    }
                })
            } else {
                // Fallback to default required fields if formFields not provided
                const defaultRequiredFields = ['first_name', 'last_name', 'email']
                defaultRequiredFields.forEach(field => {
                    const value = dynamicFormData[field] || ''
                    if (!value.trim()) {
                        checkOk = false
                        errors[field] = reduxText[7489]
                    }
                })
            }

            // Set errors
            console.log(errors)
            setDynamicFormErrors(errors)
            return checkOk
        } catch (error) {
            axiosErrorHandlerSite({
                apiUrl: 'apiUrl',
                component: 'ButtonHelper810Site',
                dispatch,
                error,
                reduxAuth,
                reduxModalSite,
                reference: 'handleCheckData',
            })
        }
    }

    function handleStripe() {
        try {
            if (!handleCheckData()) return
            setIsLoading(true)
            const formData = new FormData()
            formData.append('amount', Number(amount).toFixed(2))
            formData.append('currency', currency)
            formData.append('path', `https://${window.location.hostname}${window.location.pathname}`)
            formData.append('product_id', productId!)

            if (membershipPriceId) {
                formData.append('membership_price_id', membershipPriceId)
            }

            if (isRecurent) {
                formData.append('mode', 'subscription')
            }

            // Add all form data
            Object.entries(dynamicFormData).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    // Stringify JSON objects and arrays, otherwise convert to string
                    const stringValue = (value && typeof value === 'object')
                        ? JSON.stringify(value)
                        : String(value)
                    formData.append(key, stringValue)
                }
            })
            const postUrl = getApiUrlSiteV2(`${api_url_stripe_subscription_customer_create_web}${reduxCacheSite.portfolio?.hostname_slug}/`, reduxModalSite)
            axios({
                data: formData,
                headers: getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch),
                method: 'post',
                url: postUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    setIsLoading(false)
                    stripe!.redirectToCheckout({
                        // Make the id field from the Checkout Session creation API response
                        // available to this file, so you can provide it as parameter here
                        // instead of the {{CHECKOUT_SESSION_ID}} placeholder.
                        sessionId: response.data.id,
                    }).then((result: any) => {
                        if (process.env.NODE_ENV === 'development') console.log(result)
                        // If `redirectToCheckout` fails due to a browser or network
                        // error, display the localized error message to your customer
                        // using `result.error.message`.
                    })
                })
                .catch((error) => {
                    setIsLoading(false)
                    axiosErrorHandlerSite({
                        apiUrl: postUrl,
                        component: 'ButtonHelper810Site',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleStripe',
                    })
                })
        } catch (error) {
            setIsLoading(false)
            axiosErrorHandlerSite({
                apiUrl: 'postUrl',
                component: 'ButtonHelper810Site',
                dispatch,
                error,
                reduxAuth,
                reduxModalSite,
                reference: 'handleStripe',
            })
        }
    }

    return (
        <React.Fragment>
            <button
                className={`t-810-button ${deviceType}`}
                disabled={isLoading}
                onClick={() => handleStripe()}
                type='button'
            >
                {buttonText || reduxText[8424]}
            </button>
            {isLoading && (
                <LoaderSite isOpen />
            )}
        </React.Fragment>
    )
})
